/**
 * DuplicatePoWarning — amber banner shown when a (customer, PO) pair
 * already matches one or more existing orders. Non-blocking; the caller
 * decides whether to allow the user to continue.
 *
 * Two kinds of match (kanban f1f3600c):
 *  - `open`     — a live portal order: suggest adjusting it instead of
 *                 creating a new one.
 *  - `invoiced` — the PO already shipped and was billed (a completed
 *                 portal order, or Sage invoice history for a PO keyed
 *                 straight into Sage). AU02235 duplicated the invoiced
 *                 AU01536 because this case produced no banner; the
 *                 wording asks "is this a re-order?" and names the
 *                 invoice date. A Sage-only match has no portal order to
 *                 open, so it gets no "View Existing Order" button.
 *
 * Used from CreateOrderDialog and OrderDetailModal (the latter is also
 * where Submit to Sage lives, so the invoiced warning is on screen at
 * submit time).
 *
 * Audit trail: showing the banner and clicking through to an order are
 * recorded as portal events (`duplicate_po_warning_shown`,
 * `duplicate_po_view_order`).
 */
import React, { useEffect } from 'react';
import type { DuplicatePoOrderSummary } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import {
  DUPLICATE_PO_STATUS_LABELS,
  duplicateInvoicedWhen,
  duplicateOrderLabel,
  duplicatePoCopy,
} from './duplicatePoCopy';
import styles from './DuplicatePoWarning.module.css';

interface DuplicatePoWarningProps {
  duplicates: DuplicatePoOrderSummary[];
  /** Called when the user clicks "View Existing Order" — receives the order id. */
  onViewOrder: (orderId: string) => void;
}

export const DuplicatePoWarning: React.FC<DuplicatePoWarningProps> = ({
  duplicates,
  onViewOrder,
}) => {
  const signature = duplicates
    .map((d) => `${d.match_kind}:${d.id ?? d.sage_order_no ?? d.invoice_no ?? ''}`)
    .join(',');

  useEffect(() => {
    if (duplicates.length === 0) return;
    trackEvent('view', 'duplicate_po_warning_shown', {
      po_number: duplicates.find((d) => d.po_number)?.po_number ?? null,
      match_count: duplicates.length,
      open_count: duplicates.filter((d) => d.match_kind !== 'invoiced').length,
      invoiced_count: duplicates.filter((d) => d.match_kind === 'invoiced').length,
      order_ids: duplicates.map((d) => d.id).filter((id): id is string => !!id),
      sage_order_nos: duplicates
        .map((d) => d.sage_order_no)
        .filter((so): so is string => !!so),
    });
    // The signature captures identity + kind of every match; re-render
    // with the same set must not re-record the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (duplicates.length === 0) return null;

  const { headline, subtitle } = duplicatePoCopy(duplicates);
  const onlyInvoiced = duplicates.every((d) => d.match_kind === 'invoiced');

  return (
    <div
      className={styles.banner}
      role="alert"
      data-testid="duplicate-po-warning"
      data-match-kind={onlyInvoiced ? 'invoiced' : 'open'}
    >
      <span className={styles.icon} aria-hidden="true">
        &#9888;
      </span>
      <div className={styles.body}>
        <div className={styles.title}>{headline}</div>
        <div className={styles.subtitle}>{subtitle}</div>
        <ul className={styles.list}>
          {duplicates.map((d, idx) => {
            const label = duplicateOrderLabel(d);
            const statusLabel = DUPLICATE_PO_STATUS_LABELS[d.status] ?? d.status;
            const isInvoiced = d.match_kind === 'invoiced';
            const when = duplicateInvoicedWhen(d);
            const key = d.id ?? d.sage_order_no ?? d.invoice_no ?? String(idx);
            return (
              <li
                key={key}
                className={styles.item}
                data-testid="duplicate-po-item"
                data-match-kind={d.match_kind}
              >
                <div className={styles.itemMeta}>
                  <span className={styles.orderNo}>{label}</span>
                  <span className={styles.statusPill}>{statusLabel}</span>
                  {isInvoiced && (
                    <span className={styles.invoicedNote}>
                      {when ? `Invoiced ${when}` : 'Invoiced'}
                      {d.invoice_no && d.sage_order_no ? ` (invoice ${d.invoice_no})` : ''}
                    </span>
                  )}
                  {d.id && (
                    <span className={styles.lineCount}>
                      {d.line_item_count} line item{d.line_item_count === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                {d.id ? (
                  <button
                    type="button"
                    className={styles.viewButton}
                    onClick={() => {
                      trackEvent('click', 'duplicate_po_view_order', {
                        order_id: d.id,
                        sage_order_no: d.sage_order_no,
                        match_kind: d.match_kind,
                      });
                      onViewOrder(d.id as string);
                    }}
                  >
                    View Existing Order
                  </button>
                ) : (
                  <span className={styles.sageOnly}>Keyed in Sage, not in the portal</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default DuplicatePoWarning;
